import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";

import Button from "components/ui/Button";
import Logo from "components/layout/Logo";
import { FRONT_URL } from "lib/config";

// The site header, same markup and styles (.yc-header in styles/ui.css) as
// the frontpage's components/layout/SiteHeader: logo, nav, class search,
// then Log In (signed out) or the account menu (signed in).
function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

// Three bars, or a cross while the mobile menu is open.
function MenuIcon({ open }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  );
}

function AvatarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <circle cx="12" cy="8.5" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 19.5c1.2-3.3 4-5 7-5s5.8 1.7 7 5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function UserMenu({ user }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const item = (to, label, exact) => (
    <NavLink exact={exact} to={to} role="menuitem" onClick={() => setOpen(false)}>
      {label}
    </NavLink>
  );
  return (
    <div className="yc-user" ref={box}>
      <button type="button" className="yc-user-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="yc-user-name">{user.name}</span>
        <span className="yc-user-avatar">{user.avatar ? <img src={user.avatar} alt="" /> : <AvatarIcon />}</span>
        <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
          <path d="M0 0h10L5 6z" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div className="yc-user-menu" role="menu">
          {item("/", "My classes", true)}
          {item("/settings", "Profile", true)}
          {item("/settings/password", "Password")}
          {item("/transactions", "Order history")}
          <Link to="/logout" role="menuitem" className="yc-user-menu-logout">Log out</Link>
        </div>
      )}
    </div>
  );
}

export default function SiteHeader() {
  const user = useSelector((state) => state.users);
  const signedIn = Boolean(user);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // The mobile menu closes on Escape and when a link in it is followed.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function search(e) {
    e.preventDefault();
    const q = e.target.q.value.trim();
    window.location.href = q ? `${FRONT_URL}/courses?q=${encodeURIComponent(q)}` : `${FRONT_URL}/courses`;
  }

  const searchForm = (id) => (
    <form className="yc-search" role="search" onSubmit={search}>
      <SearchIcon />
      <label className="sr-only" htmlFor={id}>Search classes</label>
      <input id={id} name="q" placeholder="Search classes" />
    </form>
  );
  const links = (
    <>
      <a href={`${FRONT_URL}/courses`}>All classes</a>
      {signedIn && (
        <NavLink exact to="/" onClick={() => setMenuOpen(false)}>
          My classes
        </NavLink>
      )}
    </>
  );

  return (
    <header className={`yc-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="yc-container yc-header-inner">
        <Logo />
        <nav className="yc-nav">{links}</nav>
        {searchForm("yc-search")}
        <div className="yc-actions">{signedIn ? <UserMenu user={user ?? {}} /> : <Button to="/login">Log In</Button>}</div>
        <button
          type="button"
          className="yc-menu-btn"
          aria-expanded={menuOpen}
          aria-controls="yc-mobile-menu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <MenuIcon open={menuOpen} />
          {menuOpen ? "CLOSE" : "MENU"}
        </button>
      </div>
      {menuOpen && (
        <div id="yc-mobile-menu" className="yc-mobile-menu">
          <div className="yc-container">
            {searchForm("yc-search-mobile")}
            <nav>{links}</nav>
          </div>
        </div>
      )}
    </header>
  );
}
